import { forwardRef, Inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, RouterStateSnapshot } from '@angular/router';
import { Store } from '@ngrx/store';
import { DependencyTreeCanActivate } from 'app/core/guards/dependency-tree.guard';
import { UserContextLoadGuard } from 'app/core/guards/user-context-load.guard';
import { AuthorizationGuard } from 'app/core/store/session/authorization/authorization.resolve';
import { logger } from 'app/core/util/logger';
import { Observable, of } from 'rxjs';
import * as fromRoot from '../store';
import { AdminDefinitionsLoadAction } from '../store/admin/admin-definitions/admin-definitions.actions';
import { AdminDefinitions } from '../store/admin/admin-definitions/admin-definitions.interface';
import { RootState } from '../store/index';
import { filter, first, switchMap } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class AdminDefinitionsGuard implements CanActivate, DependencyTreeCanActivate {
  private store: Store<fromRoot.RootState>;

  constructor(@Inject(forwardRef(() => Store)) store) {
    this.store = store;
  }

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> | Promise<boolean> | boolean {
    logger.debug(`In AdminDefinitionsGuard`);
    return this.store.pipe(
      filter((root: fromRoot.RootState) => root.session.userContext.contextLoaded),
      first(),
      switchMap((root: RootState) => {
        logger.debug('inner GetAdminDefinitionsGuard');
        if (root.admin.adminDefinitions.loaded) return of(true);

        this.store.dispatch(new AdminDefinitionsLoadAction({ language: root.session.userContext.language }));
        return this.store
          .select(store => store.admin.adminDefinitions)
          .pipe(
            filter((definitions: AdminDefinitions) => definitions.loaded),
            first(),
            switchMap(() => of(true))
          );
      })
    );
  }

  getDependencies() {
    return [UserContextLoadGuard, AuthorizationGuard];
  }

  getDependencyRouteData() {
    return { authorization: 'isAdmin' };
  }
}
